import {ConfigMetaData, keyToPath, pathToKey} from "./annotations";
import {ConfigurationSettingListWithSource} from "./settings";


const DEFAULT_URL = "https://configuration.kahun.com";

type FetchFunction = typeof fetch;

interface ConfigDataAccessOptions {
  fetch?: FetchFunction;
  url?: string;
}

class CachedFetchAccess<T> {
  constructor(private fetch?: FetchFunction) {}
  private cachedResults: Record<string, Promise<T>> = {};

  private async doFetch(url: string): Promise<T> {
    let ourFetch = this.fetch || fetch;
    console.log("Fetching", url);
    return (await ourFetch(url)).json();
  }

  protected fetchWithCache(
    key: string,
    urlFunc: (key?: string) => string,
  ): Promise<T> {
    if (!this.cachedResults[key]) {
      this.cachedResults[key] = this.doFetch(urlFunc(key));
    }
    return this.cachedResults[key];
  }
}

export class ConfigMetaDataAccess extends CachedFetchAccess<ConfigMetaData[]> {
  public constructor(private options: ConfigDataAccessOptions = {}) {
    super(options?.fetch);
  }

  private mapByKey: Record<string, ConfigMetaData>;
  private mapParentToChildren: Record<string, ConfigMetaData[]>;
  private ready = false;

  private mapData(flat: ConfigMetaData[]) {
    this.mapByKey = {};
    this.mapParentToChildren = {};

    flat.forEach((c) => {
      this.mapByKey[c.key] = c;
      const parent = c.path.join(".").trim();
      (this.mapParentToChildren[parent] ||= []).push(c);
    });

    // fill in parents
    Object.keys(this.mapParentToChildren)
      .map((key) => this.mapByKey[key])
      .forEach((subobject) => {
        if (ConfigMetaData.isSubObject(subobject)) {
          const parents = subobject.path
            ?.map((k, i, ar) => pathToKey(ar.slice(0, i + 1)))
            .map((k) => this.mapByKey[k]);
          subobject.parents = parents as any;
        }
      });
    this.ready = true;
  }

  protected async fetchFlat() {
    const flat = await this.fetchWithCache(
        "flat",
        () => `${this.options.url || DEFAULT_URL}/metadata/flat-config.json`,
    );
    return flat;
  }

  private async loadData() {
    if (!this.ready) {
      const flat = await this.fetchFlat();
      this.mapData(flat);
    }
  }

  public async getMetadataForKey(
    dottedKey: string = "",
  ): Promise<ConfigMetaData[]> {
    await this.loadData();
    const origKey = dottedKey;
    dottedKey = pathToKey(keyToPath(dottedKey).map(k=>k.startsWith('[')&&k.endsWith(']')? "[]": k));
    const configs = this.mapParentToChildren[dottedKey];

    // fix dynamic keys
    if (dottedKey!=origKey) {
      return configs.map(c=>({
        ...c,
        key: c.key.replace(dottedKey, origKey),
        path: keyToPath(c.key.replace(dottedKey, origKey))
      }))
    }
    return configs;
  }

  /***
   * Use this function to get all unfiltered metadata, from root to children
   * used for implementing search
   */
  public async getAllMettaData(): Promise<ConfigMetaData[]> {
    await this.loadData();
    return [...Object.values(this.mapByKey || {})];
  }
}

export class ConfigEnvironmentSettingsAccess extends CachedFetchAccess<ConfigurationSettingListWithSource> {
  public constructor(private options: ConfigDataAccessOptions = {}) {
    super(options?.fetch);
  }

  public async getSettings(
    env: string = "",
  ): Promise<ConfigurationSettingListWithSource> {
    const url = () => {
      return `${this.options.url || DEFAULT_URL}/env/${env}/settings.json`;
    };
    try {
      return (await this.fetchWithCache(env, url)) || {};
    } catch (e) {
      console.log("problems loading env", env);
      console.log(e);
      return {};
    }
  }
}
